import React from 'react';
import PropTypes from 'prop-types';
import {
  formatGradePercent,
  formatGradePoints,
  getOutOfLabel,
  shouldShowPointDecimals,
  GRADEBOOK_SETTINGS,
} from './utils';
import styles from './AverageRow.module.css';

export function AverageRow({
  stickyColumns,
  scrollableColumns,
  virtualColumns,
  paddingLeft,
  paddingRight,
  averageRow,
  focusedCell,
  handleCellClick,
  showPercent,
  roundPercentType,
  settings,
  columnIndexMap,
  hasStudents = true,
}) {
  // Helper to format average values - supports both percent and points mode
  const formatAverageGrade = (gradeData, hasStudents) => {
    if (!gradeData) return '—';

    const { score, pointsEarned, pointsPossible } = gradeData;

    if (showPercent) {
      // Show percentage
      if (score === null || score === undefined) return '—';
      if (!hasStudents) return '--';
      const grade = {
        studentGrade: score / 100,
        isAssigned: true,
      };
      return formatGradePercent(
        grade.studentGrade,
        grade,
        settings,
        roundPercentType,
      );
    } else {
      // Show points with "Out of X" label
      if (pointsEarned === null || pointsEarned === undefined) return '—';
      const grade = {
        studentPoints: pointsEarned,
        assigned_points: pointsPossible,
        isAssigned: true,
        tool: gradeData.tool,
      };
      let points = formatGradePoints(grade, settings);
      if (!hasStudents) points = '--';
      const showDecimals = shouldShowPointDecimals(grade);
      const outOf = getOutOfLabel(pointsPossible, showDecimals);
      return (
        <>
          <span className={styles.pointsValue}>{points}</span>
          {outOf && <small className={styles.outOf}>{outOf}</small>}
        </>
      );
    }
  };

  return (
    <tr
      className={styles.row}
      role="row"
      aria-rowindex={3}
      aria-label="Class averages"
    >
      {/* Render sticky name column */}
      {stickyColumns.map((column) => {
        const isFocused =
          focusedCell?.rowIndex === -1 && focusedCell?.columnId === column.id;

        return (
          <th
            key={column.id}
            className={`${styles.stickyNameCell} ${isFocused ? styles.focusedCell : ''}`}
            style={{
              position: 'sticky',
              left: 0,
              width: '250px',
              minWidth: '250px',
              maxWidth: '250px',
              zIndex: 50,
            }}
            role="rowheader"
            scope="row"
            tabIndex={isFocused ? 0 : -1}
            aria-colindex={1}
            data-row-index={-1}
            data-column-id={column.id}
            data-grid-id={`r2-c${columnIndexMap?.get(column.id) ?? 0}`}
            onClick={() => column.id && handleCellClick(-1, column.id)}
          >
            Average
          </th>
        );
      })}
      {/* Padding for horizontal scroll - always render for stable layout */}
      <td
        style={{
          width: paddingLeft,
          minWidth: paddingLeft,
          maxWidth: paddingLeft,
          padding: 0,
          border: 'none',
        }}
      />
      {/* Render virtual columns */}
      {virtualColumns.map((virtualColumn) => {
        const column = scrollableColumns[virtualColumn.index];
        if (!column) return <td key={`empty-${virtualColumn.index}`} />;

        const isFocused =
          focusedCell?.rowIndex === -1 && focusedCell?.columnId === column.id;

        let displayValue;
        if (column.id === 'courseTotal') {
          // Course Total column shows empty in the Average row (matches V1 behavior)
          displayValue = '';
        } else if (column.id === 'studentId') {
          // Student ID column shows empty in the Average row (matches V1 behavior)
          displayValue = '';
        } else if (column.id && column.id.startsWith('category-total-')) {
          // Category Total columns show empty in the Average row (matches V1 behavior)
          displayValue = '';
        } else if (column.id && averageRow.grades[column.id]) {
          const gradeData = averageRow.grades[column.id];
          // Check if this is an ungraded assignment - show "X students completed" instead of percentage
          if (gradeData.isUngraded) {
            const numCompleted = gradeData.numCompleted || 0;
            displayValue = (
              <div className={styles.ungradedHeaderContainer}>
                <span className={styles.ungradedHeaderNumbers}>
                  {numCompleted}{' '}
                </span>
                <span className={styles.ungradedDescriptionText}>
                  students completed
                </span>
              </div>
            );
          } else {
            // Assignment averages - pass full gradeData for points/percent formatting
            displayValue = formatAverageGrade(gradeData, hasStudents);
          }
        } else {
          displayValue = '—';
        }

        return (
          // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
          <td
            key={column.id}
            className={`${styles.averageCell} ${isFocused ? styles.focusedCell : ''}`}
            style={{
              width: `${virtualColumn.size}px`,
              minWidth: `${virtualColumn.size}px`,
              maxWidth: `${virtualColumn.size}px`,
            }}
            role="gridcell"
            tabIndex={isFocused ? 0 : -1}
            aria-colindex={(columnIndexMap?.get(column.id) ?? 0) + 1}
            data-row-index={-1}
            data-column-id={column.id}
            data-grid-id={`r2-c${columnIndexMap?.get(column.id) ?? 0}`}
            onClick={() => column.id && handleCellClick(-1, column.id)}
          >
            {displayValue}
          </td>
        );
      })}
      {/* Padding for horizontal scroll (right) - for columns after visible ones */}
      <td
        style={{
          width: paddingRight,
          minWidth: paddingRight,
          maxWidth: paddingRight,
          padding: 0,
          border: 'none',
        }}
      />
    </tr>
  );
}

AverageRow.propTypes = {
  stickyColumns: PropTypes.array.isRequired,
  scrollableColumns: PropTypes.array.isRequired,
  virtualColumns: PropTypes.array.isRequired,
  paddingLeft: PropTypes.number.isRequired,
  paddingRight: PropTypes.number.isRequired,
  averageRow: PropTypes.shape({
    courseTotal: PropTypes.number,
    categoryTotals: PropTypes.object,
    grades: PropTypes.object,
  }).isRequired,
  focusedCell: PropTypes.shape({
    rowIndex: PropTypes.number,
    columnId: PropTypes.string,
  }),
  handleCellClick: PropTypes.func.isRequired,
  showPercent: PropTypes.bool,
  roundPercentType: PropTypes.number,
  settings: PropTypes.array,
  columnIndexMap: PropTypes.instanceOf(Map),
  hasStudents: PropTypes.bool,
};

AverageRow.defaultProps = {
  focusedCell: null,
  showPercent: true,
  roundPercentType: GRADEBOOK_SETTINGS.roundPercentWhole,
  settings: [],
  columnIndexMap: null,
};

export default AverageRow;
